import { beschriftung, katalog, t } from './sprache'
import { ohneWert } from './Ohnewert'
import { nichtUmgerechnetIn } from './Anzeigewaehrung'
import { kreuzzelle, wertAuf } from './Aufriss'
import type { Kfrreferenzwert } from './typen'
import type { Zelle } from './Aufriss'
import type { Achsenquelle } from './Aufrisszeilen'
import { istTrefferzelle } from './Bericht'
import type { Aufrissbedienung, Trefferzustand } from './Bericht'
import { partnerIn } from './BerichtStruktur'
import { Ergebnisgliederung, useErgebnisgliederung } from './Ergebnisgliederung'
import type { Strukturbild } from './BerichtStruktur'
import {
  einheitenIn,
  herkunftAusMenue,
  Kfrnachweis,
  Kfrtabelle,
  Leer,
  nachweisruf,
  Probezelle,
  Zahl,
} from './Kapitalflussrechnung'
import type { Kfranlageruf, Kfrnachweisruf } from './Kapitalflussrechnung'
import type {
  Berichtszeile,
  Kapitalflussrechnung as Kfrsatz,
  Kfrbeitrag,
  Kfrzeilenwert,
  Kfrzusatzzelle,
} from './typen'

/**
 * Die Kapitalflussrechnung in den Bildern, deren Spalten eine Achse tragen —
 * **Konzernstruktur**, **verkürzter Konzernaufriss** und **Partner** —
 * dieselben drei wie in Bilanz und GuV (R167), mit denselben Regeln:
 *
 * **In den Achsenspalten steht die Handelsbilanz II**, wie im Bericht (R180):
 * was die Gesellschaft selbst beisteuert, vor jeder Eliminierung. Erst so ergibt
 * die Zeile das Schema, das ein Fachanwender im Kopf hat — Summe der
 * Einzelbeiträge plus Konsolidierung gleich Konzern. Im Partnerbild ist es
 * dieselbe Zahl, gegen den Partner geschnitten; mit gewählter Gesellschaft
 * nur ihre, und ohne Konsolidierung und Konzern, weil die keine Größen einer
 * einzelnen Gesellschaft sind.
 */
export function KapitalflussNachStruktur({
  rechnung,
  plan,
  bild,
  gesellschaft = null,
  partnerzellen = [],
  aufriss,
  quelle,
  treffer,
  aufAenderung,
  aufNachweis,
  aufAnlegen,
}: {
  rechnung: Kfrsatz
  /** Die Gliederung, in der die GuV darunter gelesen wird (R489) — dieselbe wie im Bericht. */
  plan?: string | null
  bild: Strukturbild
  gesellschaft?: string | null
  /** Im Partnerbild: der Würfel je Partner, auf Abruf geholt (7.3 Abschnitt 2f). */
  partnerzellen?: Kfrzusatzzelle[]
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Kfrbeitrag>
  treffer?: Trefferzustand
  aufAenderung?: () => void
  aufNachweis?: Kfrnachweisruf
  /** Rechtsklick → Unterzeile oder Zeile danach (R494, R1124). */
  aufAnlegen?: Kfranlageruf
}) {
  // Vor dem fruehen Ausstieg: ein Haken vertraegt keine Bedingung.
  const guv = useErgebnisgliederung(rechnung.grundlage, plan)

  if (rechnung.zeilen.length === 0) {
    return <Leer rechnung={rechnung} />
  }

  const nach = katalog()
  const achse = bild === 'struktur' ? 'einheiten' : bild === 'partner' ? 'partner' : null
  const spalten: (string | null)[] =
    bild === 'struktur' ? einheitenIn(rechnung) : bild === 'partner' ? partnerIn(partnerzellen) : []
  const mitKonzern = gesellschaft === null

  function kopf(kennung: string | null): React.ReactNode {
    if (kennung === null) {
      return ohneWert('weitere')
    }

    const e =
      achse === 'partner'
        ? (nach.partner.get(kennung) ?? nach.einheiten.get(kennung))
        : nach.einheiten.get(kennung)
    return (
      <>
        {e?.code ?? kennung}
        {e && beschriftung(e.namen) && <span className="spaltenname">{beschriftung(e.namen)}</span>}
      </>
    )
  }

  const summenkopf = (() => {
    if (gesellschaft === null) {
      return t('spalte.summe')
    }

    const e = nach.einheiten.get(gesellschaft)
    return (
      <>
        {e?.code ?? gesellschaft}
        {e && beschriftung(e.namen) && <span className="spaltenname">{beschriftung(e.namen)}</span>}
      </>
    )
  })()

  function hb2Von(z: Zelle & Partial<Kfrbeitrag>, vorrat: Kfrbeitrag[]): number {
    if (gesellschaft === null) {
      return z.hb2 ?? 0
    }

    return kreuzzelle(vorrat, z, 'einheiten', gesellschaft)?.hb2 ?? 0
  }

  function spaltenwert(zeile: string, z: Zelle, vorrat: Kfrbeitrag[], kennung: string | null): number {
    if (achse === 'einheiten') {
      return kreuzzelle(vorrat, z, 'einheiten', kennung)?.hb2 ?? 0
    }

    const eigen = wertAuf(z, 'partner')
    if (eigen !== undefined) {
      return eigen === kennung ? hb2Von(z, vorrat) : 0
    }

    if (Object.keys(z.weitere ?? {}).length > 0) {
      return 0
    }

    const einheit = z.einheit ?? gesellschaft
    return (
      partnerzellen.find(
        (p) => p.zeile === zeile && p.wert === kennung && p.einheit === einheit && p.konto === z.konto,
      )?.hb2 ?? 0
    )
  }

  const spaltenkennung = (kennung: string | null) => `${achse}:${kennung ?? '∅'}`

  /** Die Zelle, die eine Achsenspalte für den Nachweis meint. */
  const spaltenzelle = (z: Zelle, kennung: string | null): { einheit: string | null; konto: string | null; weitere?: Record<string, string | null> } =>
    achse === 'einheiten'
      ? { einheit: kennung, konto: z.konto, weitere: z.weitere }
      : { einheit: z.einheit ?? gesellschaft, konto: z.konto, weitere: { ...z.weitere, partner: kennung } }

  /*
    **Eine Referenz als eigene Zeile** (2.1 Abschnitt 3.6, R1710): je
    Gesellschaft ihr Beitrag. Nach Partnern bleiben ihre Zellen leer — die
    Rechnung schneidet eine Referenz nicht nach Partnern.
  */
  const ohneZelle = { einheit: null, konto: null }
  const referenzwerte = (r: Kfrreferenzwert) => (
    <>
      {spalten.map((s) =>
        achse === 'einheiten' ? (
          <Zahl key={s ?? '∅'} wert={kreuzzelle(r.beitraege, ohneZelle, 'einheiten', s)?.hb2 ?? 0} spalte="hb2" />
        ) : (
          <td key={s ?? '∅'} className="zahl" />
        ),
      )}
      <Zahl
        wert={gesellschaft === null ? r.hb2 : (kreuzzelle(r.beitraege, ohneZelle, 'einheiten', gesellschaft)?.hb2 ?? 0)}
        klasse="spaltensumme"
        spalte="hb2"
      />
      {mitKonzern && <Zahl wert={r.betrag - r.hb2} />}
      {mitKonzern && <Zahl wert={r.betrag} klasse="konzern" spalte="konzern" />}
    </>
  )

  const werte = (zeile: Kfrzeilenwert, z: Zelle & Partial<Kfrbeitrag>, kennung: string, vorrat: Kfrbeitrag[]) => (
    <>
      {spalten.map((s) => {
        const ziel = spaltenzelle(z, s)
        return (
          <Zahl
            key={s ?? '∅'}
            wert={spaltenwert(zeile.zeile, z, vorrat, s)}
            fund={istTrefferzelle(treffer, kennung, spaltenkennung(s))}
            spalte="hb2"
            aufNachweis={nachweisruf(aufNachweis, zeile, ziel.einheit, ziel.konto, 'hb2', undefined, ziel.weitere)}
          />
        )
      })}
      <Zahl
        wert={hb2Von(z, vorrat)}
        klasse="spaltensumme"
        fund={istTrefferzelle(treffer, kennung, 'summe')}
        spalte="hb2"
        aufNachweis={nachweisruf(aufNachweis, zeile, z.einheit ?? gesellschaft, z.konto, 'hb2', undefined, z.weitere)}
      />
      {/*
        **Die Konsolidierungsspalte ist eine Differenz**, keine eigene
        Spalte des Ergebnisses — dieselbe Entscheidung wie im Bericht. Und
        deshalb trägt sie auch keinen Nachweis: Er müsste eine Differenz
        zweier Spalten erklären, und das ist keine Herkunft (R213).
      */}
      {mitKonzern && (
        <Zahl wert={(z.betrag ?? 0) - (z.hb2 ?? 0)} fund={istTrefferzelle(treffer, kennung, 'konsolidierung')} />
      )}
      {mitKonzern && (
        <Zahl
          wert={z.betrag ?? 0}
          klasse="konzern"
          fund={istTrefferzelle(treffer, kennung, 'konzern')}
          spalte="konzern"
          aufNachweis={nachweisruf(aufNachweis, zeile, z.einheit, z.konto, 'konzern', undefined, z.weitere)}
        />
      )}
    </>
  )

  return (
    <div className="tabellenrahmen">
      <Kfrtabelle<Kfrbeitrag, Kfrzeilenwert>
        standard={rechnung.standard}
        aufAnlegen={aufAnlegen}
        aufAenderung={aufAenderung}
        kennung={`kfr:${bild}`}
        waehrung={nichtUmgerechnetIn(rechnung.umrechnung)}
        zeilen={rechnung.zeilen}
        spaltenzahl={2 + spalten.length + (mitKonzern ? 3 : 1)}
        kopfzellen={
          <>
            {spalten.map((s) => (
              <th key={s ?? '∅'} className="zahl">
                {kopf(s)}
              </th>
            ))}
            <th className="zahl spaltensumme">{summenkopf}</th>
            {mitKonzern && <th className="zahl">{t('spalte.konsolidierung')}</th>}
            {mitKonzern && <th className="zahl konzern">{t('spalte.konzern')}</th>}
          </>
        }
        werte={(zeile, id) =>
          werte(
            zeile,
            { einheit: null, konto: null, hb2: zeile.hb2, betrag: zeile.betrag },
            id,
            zeile.beitraege,
          )
        }
        // **Die Aufrisszeile trägt dieselben Spalten wie die Zeile darüber**
        // (R180/R212): Eine aufgeklappte Kontozeile zeigt ihren Beitrag je
        // Gesellschaft, nicht nur in der Summe. Die Kreuzzelle dafür führt
        // der Würfel bereits mit — beim Abruf auf einer Zusatzachse der Abruf.
        aufrisswerte={(z, kennung, zeile, pfad) =>
          werte(zeile, z, kennung, aufriss.vorratFuer(quelle, zeile.beitraege, zeile.zeile, pfad))
        }
        referenzen={(zeile) => (zeile.referenzen ?? []).map((r) => ({ ...r, werte: referenzwerte(r) }))}
        aufriss={aufriss}
        quelle={quelle}
        treffer={treffer}
        herkunft={(zeile, pfad, kontext) => herkunftAusMenue(aufNachweis, zeile, pfad, kontext)}
        /*
          **Die Kontrollzeile** (R1019) — unter den Spalten, die den
          Finanzmittelfonds am Ende zeigen: Summe der Handelsbilanzen II,
          Konsolidierung, Konzern. Die Spalten je Gesellschaft oder Partner
          tragen seit R1067 ihre Summenzeilen, aber keinen eigenen
          Endbestand des Fonds, gegen den sie sich prüfen ließen — sie
          bleiben hier deshalb leer.
        */
        kontrolle={
          <>
            {spalten.map((s) => (
              <Probezelle key={s ?? '∅'} wert={undefined} />
            ))}
            <Probezelle wert={rechnung.fondsprobe?.hb2} klasse="spaltensumme" />
            {mitKonzern && (
              <Probezelle
                wert={rechnung.fondsprobe ? rechnung.abweichung - rechnung.fondsprobe.hb2 : undefined}
              />
            )}
            {mitKonzern && <Probezelle wert={rechnung.abweichung} klasse="konzern" />}
          </>
        }
        /*
          **Die GuV-Gliederung unter dem Periodenergebnis** (R489) — aber nur
          dort, wo ihre Spalten dieselben sind.

          Im Bild *Partner* sind sie es nicht: Die Partnerspalten kommen aus
          einem eigenen Abruf der Kapitalflussrechnung (`partnerzellen`), und
          den gibt es für die GuV auf diesem Bildschirm nicht. Lieber kein
          Angebot als eine Spalte voller Nullen, die aussieht wie eine Aussage.
        */
        gliederung={
          achse === 'partner'
            ? undefined
            : () => (
                <Ergebnisgliederung<Berichtszeile>
                  zeilen={guv.zeilen}
                  anfordern={guv.anfordern}
                  spaltenzahl={2 + spalten.length + (mitKonzern ? 3 : 1)}
                  vorspalte={<td className="kfrnummer" />}
                  werte={(z) => (
                    <>
                      {spalten.map((s) => (
                        <Zahl
                          key={s ?? '∅'}
                          wert={
                            kreuzzelle(z.beitraege, { einheit: null, konto: null }, 'einheiten', s)?.hb2 ?? 0
                          }
                        />
                      ))}
                      <Zahl wert={z.hb2} klasse="spaltensumme" />
                      {mitKonzern && <Zahl wert={z.konzern - z.hb2} />}
                      {mitKonzern && <Zahl wert={z.konzern} klasse="konzern" />}
                    </>
                  )}
                />
              )
        }
      />

      <Kfrnachweis rechnung={rechnung} />
    </div>
  )
}
